import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'

/**
 * Why the Save button is grey.
 *
 * Sixty-eight fields on this desk and not one of them is marked optional, so
 * every form looks equally compulsory — and a form whose Save is disabled
 * says nothing at all about which box is empty. On the invitation panel that
 * is five fields to hunt through; on the event editor the missing one is
 * usually the registration link, three sections below the fold.
 *
 * A dead end becomes an instruction. Nothing is drawn when nothing is
 * missing, so a finished form is not nagged at, and the line sits beside the
 * button rather than above the fields, because that is where somebody is
 * looking when they wonder why they cannot press it.
 *
 * ## And then it takes you there
 *
 * Naming the field was half of it. The sentence above already admitted the
 * other half — *"on the event editor the missing one is usually the
 * registration link, three sections below the fold"* — and telling somebody
 * that a box three sections away is empty still leaves them to find it.
 *
 * A field given with an `id` is drawn as a button that scrolls to it and puts
 * the cursor in it. One press instead of a hunt. A field given as a bare word
 * is drawn as before, because on a five-field panel there is nothing to hunt
 * for and a link to the box beside you is noise.
 */
export interface Needed {
  /** "a registration link" — said the way somebody would say it. */
  word: string
  /** The field's `id`, so pressing the word can go to it. */
  id: string
}

export function StillNeeded({ fields }: { fields: (string | Needed)[] }) {
  if (fields.length === 0) return null
  return (
    /* `role="status"`, so somebody who has just pressed a grey Save hears why
       without going looking. The buttons inside it are reachable by tab in
       the ordinary way — the announcement does not swallow them. */
    /* `w-full` below `sm`: this is a sentence, and on a phone it took two
       lines of a footer and squeezed Save into the corner beside it. In a
       wrapping footer a full-width child takes its own line and leaves the
       buttons a row at a normal size. */
    <Text variant="caption" className="text-ink-muted w-full sm:mr-auto sm:w-auto" role="status">
      Still needed: {punctuate(fields.map(one))}
    </Text>
  )
}

function one(field: string | Needed, i: number): React.ReactNode {
  if (typeof field === 'string') return field
  return (
    <button
      key={i}
      type="button"
      onClick={() => take(field.id)}
      className={cn(
        'text-coral-ink underline decoration-dotted underline-offset-2',
        'hover:decoration-solid focus-visible:ring-coral-fill/30 rounded-xs',
        'focus-visible:ring-2 focus-visible:outline-none',
        /* An 18px-tall word is a poor thing to aim a thumb at. The padding
           grows the target and the negative margin keeps the sentence
           exactly where it was — these sit inside running text and must not
           push it around. */
        '-my-1.5 py-1.5',
      )}
    >
      {field.word}
    </button>
  )
}

/**
 * Scroll to the field and put the cursor in it.
 *
 * `focus` alone would do both in most browsers, but not reliably inside a
 * sheet's own scroller, and a focused box somebody cannot see is worse than
 * no jump at all. Scroll first, deliberately, then focus.
 */
function take(id: string): void {
  const field = document.getElementById(id)
  if (!field) return
  field.scrollIntoView({ block: 'center', behavior: 'smooth' })
  field.focus({ preventScroll: true })
}

/**
 * "a title, a date and a link" — a list as somebody would say it.
 *
 * A list of plain words stays one string rather than becoming a row of spans.
 * Splitting a sentence into elements for no reason is how a line that reads
 * fine to the eye starts being read out in pieces.
 */
function punctuate(parts: React.ReactNode[]): React.ReactNode {
  if (parts.length === 1) return parts[0]
  if (parts.every((p) => typeof p === 'string')) {
    return `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`
  }
  return parts.map((p, i) => (
    <span key={i}>
      {p}
      {i < parts.length - 2 ? ', ' : i === parts.length - 2 ? ' and ' : ''}
    </span>
  ))
}
